import AddIcon from "@mui/icons-material/Add";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import { api } from "common";
import { firebase } from "common/src/config/configureFirebase";
import { generateId } from "common/src/helper/helper";
import AlertDialog from "components/AlertDialog";
import CreateDocumentDialog from "components/Dialog/CreateDocumentDialog";
import DocumentFieldTable from "components/Table/DocumentFieldTable";
import DocumentUserTable from "components/Table/DocumentUserTable";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FirebaseConfig } from "../config/FirebaseConfig";

const DocumentManagementMenu = () => {
    console.log("🔍 DocumentManagementMenu component loaded");

    // Helper function to get auth headers (consistent with existing API patterns)
    const getAuthHeaders = async () => {
        try {
            const user = firebase.auth.currentUser;
            if (!user) {
                throw new Error("User not authenticated");
            }

            const idToken = await user.getIdToken();
            return {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
            };
        } catch (error) {
            console.error("Error getting auth headers:", error);
            throw new Error("Authentication failed: " + error.message);
        }
    };

    const {
        updateDocumentFields,
        updateDocumentUsers,
        deleteDocumentUser,
        editUser,
        updateUserField,
        fetchDocumentFields,
        fetchDocumentUsers,
        fetchUsersOnce,
    } = api;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const auth = useSelector((state) => state.auth);

    const [open, setOpen] = useState(false);
    const [editDocument, setEditDocument] = useState(null);
    const [alertDialog, setAlertDialog] = useState({
        open: false,
        title: "",
        message: "",
        type: "success",
    });

    const [rejectDialog, setRejectDialog] = useState({
        open: false,
        document: null,
        reason: "",
        isRemove: false,
    });
    const documentFields = useSelector((state) => state.documentFieldsData);
    const [documents, setDocuments] = useState([]);
    const featureFlags = useSelector((state) => state.featureflagsdata?.data);
    const addons = useSelector((state) => state.addondata?.data);

    const documentUsers = useSelector((state) => state.documentUsersData);
    const [documentUsersList, setDocumentUsersList] = useState([]);
    const [newDocumentCount, setNewDocumentCount] = useState(0);
    const [lastDocumentCount, setLastDocumentCount] = useState(0);

    console.log("🔍 DocumentManagementMenu state:", {
        documentFields,
        documentUsers,
        featureFlags,
        addons,
    });

    useEffect(() => {
        console.log(
            "🔍 DocumentManagementMenu useEffect - feature flags check"
        );
        // Temporarily disable feature flag check for testing
        // if (featureFlags && addons) {
        //   if (featureFlags.add_on === false || addons.document_management === false) {
        //     navigate('/dashboard', { replace: true });
        //   }
        // }
    }, [featureFlags, addons, navigate]);

    // Fetch data on component mount
    useEffect(() => {
        console.log("🔍 Fetching document fields and users data...");
        dispatch(fetchDocumentFields());
        dispatch(fetchDocumentUsers());
        dispatch(fetchUsersOnce());
    }, [dispatch, fetchDocumentFields, fetchDocumentUsers, fetchUsersOnce]);

    useEffect(() => {
        console.log("🔍 DocumentManagementMenu useEffect - data loading");
        try {
            // Temporarily disable feature flag check for testing
            if (documentFields && documentFields.data) {
                console.log(
                    "🔍 Document fields data found:",
                    documentFields.data
                );
                const fields = Object.values(documentFields.data);
                setDocuments(fields);
            }

            if (documentUsers && documentUsers.data) {
                console.log(
                    "🔍 Document users data found:",
                    documentUsers.data
                );
                console.log(
                    "🔍 Raw document users count:",
                    Object.keys(documentUsers.data).length
                );

                // Remove the strict filtering that was hiding valid documents
                const users = Object.values(documentUsers.data).filter(
                    (item) => {
                        // Only filter out completely invalid entries
                        const isValid =
                            item && (item.id || item.user_id || item.field_id);
                        if (!isValid) {
                            console.log(
                                "🔍 Filtered out invalid document item:",
                                item
                            );
                        }
                        return isValid;
                    }
                );

                console.log("🔍 Filtered document users count:", users.length);
                console.log(
                    "🔍 Document users details:",
                    users.map((u) => ({
                        id: u.id,
                        user_id: u.user_id,
                        field_id: u.field_id,
                        user_name: u.user_name,
                        status: u.status,
                        approved: u.approved,
                        files: u.files,
                        new_files: u.new_files,
                        files_type: typeof u.files,
                        new_files_type: typeof u.new_files,
                        files_length: u.files
                            ? Array.isArray(u.files)
                                ? u.files.length
                                : Object.keys(u.files).length
                            : 0,
                        new_files_length: u.new_files
                            ? Array.isArray(u.new_files)
                                ? u.new_files.length
                                : Object.keys(u.new_files).length
                            : 0,
                    }))
                );

                // Log full structure of first user to see all available fields
                if (users.length > 0) {
                    console.log(
                        "🔍 Full structure of first document user:",
                        users[0]
                    );
                    console.log(
                        "🔍 Available fields for sorting:",
                        Object.keys(users[0])
                    );

                    // Log raw structure from Firebase before processing
                    console.log(
                        "🔍 Raw Firebase structure of first user:",
                        JSON.stringify(
                            Object.values(documentUsers.data)[0],
                            null,
                            2
                        )
                    );
                }

                // Sort by timestamp in descending order (newest first)
                const sortedUsers = users.sort((a, b) => {
                    // Use updated_at as primary sort field (Unix timestamp in milliseconds)
                    // Fallback to created_at, then reviewed_at if updated_at not available
                    const getTimestamp = (item) => {
                        if (
                            item.updated_at &&
                            typeof item.updated_at === "number"
                        ) {
                            return item.updated_at;
                        }
                        if (
                            item.created_at &&
                            typeof item.created_at === "number"
                        ) {
                            return item.created_at;
                        }
                        if (
                            item.reviewed_at &&
                            typeof item.reviewed_at === "number"
                        ) {
                            return item.reviewed_at;
                        }
                        // Fallback to 0 if no timestamp found
                        return 0;
                    };

                    const timestampA = getTimestamp(a);
                    const timestampB = getTimestamp(b);

                    console.log(
                        `🔍 Sorting: ${a.user_name} (${timestampA}) vs ${b.user_name} (${timestampB})`
                    );

                    // Descending order (newest first) - larger timestamp comes first
                    return timestampB - timestampA;
                });

                console.log(
                    "🔍 Sorted document users by timestamp (descending)"
                );

                // Log the sorted order to verify sorting worked
                console.log(
                    "🔍 Final sorted order:",
                    sortedUsers.slice(0, 5).map((u) => ({
                        name: u.user_name,
                        updated_at: u.updated_at,
                        created_at: u.created_at,
                        reviewed_at: u.reviewed_at,
                        id: u.id,
                    }))
                );

                // Check for new documents
                if (
                    sortedUsers.length > lastDocumentCount &&
                    lastDocumentCount > 0
                ) {
                    const newCount = sortedUsers.length - lastDocumentCount;
                    setNewDocumentCount(newCount);
                    console.log(`🆕 ${newCount} new document(s) detected!`);

                    // Auto-hide notification after 5 seconds
                    setTimeout(() => {
                        setNewDocumentCount(0);
                    }, 5000);
                }

                setLastDocumentCount(sortedUsers.length);

                // Log final data that will be sent to DocumentUserTable
                console.log("🔍 Final data sent to DocumentUserTable:", {
                    count: sortedUsers.length,
                    sample_with_files: sortedUsers.slice(0, 3).map((u) => ({
                        id: u.id,
                        user_name: u.user_name,
                        files: u.files,
                        new_files: u.new_files,
                        files_type: typeof u.files,
                        new_files_type: typeof u.new_files,
                    })),
                });

                setDocumentUsersList(sortedUsers);
            }
        } catch (error) {
            console.error("🔍 Error loading document data:", error);
        }
    }, [
        documentFields,
        documentUsers,
        // featureFlags,
        // addons
    ]);

    // Add real-time refresh for document users
    useEffect(() => {
        const refreshInterval = setInterval(() => {
            console.log("🔄 Auto-refresh: Checking for new documents...");
            // Trigger a refresh of document users data
            if (documentUsers && documentUsers.data) {
                const users = Object.values(documentUsers.data).filter(
                    (item) => {
                        return (
                            item && (item.id || item.user_id || item.field_id)
                        );
                    }
                );
                setDocumentUsersList(users);
            }
        }, 10000); // Check every 10 seconds

        return () => clearInterval(refreshInterval);
    }, [documentUsers]);

    const handleOpen = () => {
        console.log("🔍 Opening create document dialog");
        setOpen(true);
    };

    const handleClose = () => {
        console.log("🔍 Closing create document dialog");
        setOpen(false);
        setEditDocument(null);
    };

    const showAlert = (title, message, type = "success") => {
        setAlertDialog({
            open: true,
            title,
            message,
            type,
        });
    };

    const closeAlert = () => {
        setAlertDialog({
            ...alertDialog,
            open: false,
        });
    };

    const handleEditDocument = (document) => {
        console.log("🔍 Editing document:", document);
        setEditDocument(document);
        setOpen(true);
    };

    const handleToggleState = (document) => {
        console.log("🔍 Toggling state for document:", document);
        try {
            dispatch(
                updateDocumentFields(
                    {
                        ...document,
                        state: !document.state,
                    },
                    () => {
                        showAlert(
                            t("success") || "Success",
                            t("updated") || "Updated successfully",
                            "success"
                        );
                    },
                    () => {
                        showAlert(
                            t("error") || "Error",
                            t("update_failed") || "Failed to update",
                            "error"
                        );
                    }
                )
            );
        } catch (error) {
            console.log("error", error);
            showAlert(t("error"), t("update_failed"), "error");
        }
    };

    const handleToggleRequired = (document) => {
        console.log("🔍 Toggling required for document:", document);
        try {
            dispatch(
                updateDocumentFields(
                    {
                        ...document,
                        required: !document.required,
                    },
                    () => {
                        showAlert(
                            t("success") || "Success",
                            t("updated") || "Updated successfully",
                            "success"
                        );
                    },
                    () => {
                        showAlert(
                            t("error") || "Error",
                            t("update_failed") || "Failed to update",
                            "error"
                        );
                    }
                )
            );
        } catch (error) {
            console.log("error", error);
            showAlert(t("error"), t("update_failed"), "error");
        }
    };

    const handleCreateDocument = (documentData, isEdit) => {
        console.log(
            "🔍 Creating/editing document:",
            documentData,
            "isEdit:",
            isEdit
        );
        try {
            const newDocument = {
                ...documentData,
                id: isEdit ? documentData.id : generateId(),
                created_at: isEdit
                    ? documentData.created_at == null
                        ? Date.now()
                        : documentData.created_at
                    : Date.now(),
            };

            console.log("🔍 Final document data to save:", newDocument);
            console.log("🔍 Generated ID:", newDocument.id);
            console.log(
                "🔍 updateDocumentFields function:",
                updateDocumentFields
            );

            dispatch(
                updateDocumentFields(
                    newDocument,
                    () => {
                        console.log("✅ Document save success callback called");
                        setEditDocument(null);
                        // Refresh data after successful save
                        dispatch(fetchDocumentFields());
                        showAlert(
                            t("success") || "Success",
                            isEdit
                                ? (t("document_updated") || "Document updated successfully")
                                : (t("document_created") || "Document created successfully"),
                            "success"
                        );
                    },
                    () => {
                        console.log("❌ Document save error callback called");
                        showAlert(
                            t("error") || "Error",
                            t("update_failed") || "Failed to update",
                            "error"
                        );
                    }
                )
            );
        } catch (error) {
            console.error("🔍 Error creating document:", error);
        }
    };

    const handleApprove = async (document) => {
        console.log("🔍 Approving document:", document);
        console.log("🔍 Document files before approval:", {
            files: document.files,
            new_files: document.new_files,
            files_type: typeof document.files,
            new_files_type: typeof document.new_files,
        });
        console.log(
            "🔍 Full document structure before approval:",
            JSON.stringify(document, null, 2)
        );
        try {
            // Check if this is a legacy license document
            const isLegacyLicense =
                document.field?.id?.startsWith("legacy_license_");

            if (isLegacyLicense) {
                console.log(
                    "🔍 Legacy license document detected, updating user profile..."
                );

                // Get the legacy field name (licenseImage or licenseImageBack)
                const legacyField =
                    document.field.id === "legacy_license_front"
                        ? "licenseImage"
                        : "licenseImageBack";

                // Get the image URL from the approved files
                const imageUrl =
                    document.files && document.files.length > 0
                        ? document.files[0].url
                        : null;

                if (imageUrl) {
                    // Update user profile with the approved license image
                    const updateData = {};
                    updateData[legacyField] = imageUrl;

                    // Update the user profile using updateUserField to only update specific fields
                    dispatch(updateUserField(document.user_id, updateData));
                    console.log(
                        `✅ User profile updated with ${legacyField}:`,
                        imageUrl
                    );
                }
            }

            // Call cloud function to update document status with notification
            console.log(
                "🔍 Calling updateDocumentStatus for approval:",
                document.id
            );
            const headers = await getAuthHeaders();
            const response = await fetch(
                `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net/updateDocumentStatus`,
                {
                    method: "POST",
                    headers,
                    body: JSON.stringify({
                        documentId: document.id,
                        status: "approved",
                    }),
                }
            );

            console.log(
                "🔍 updateDocumentStatus response status:",
                response.status
            );
            const responseData = await response.json();
            console.log("🔍 updateDocumentStatus response data:", responseData);

            if (!response.ok) {
                console.error("❌ Cloud Function failed:", responseData);
                showAlert(
                    t("error") || "Error",
                    `Cloud Function error: ${
                        responseData.error || "Unknown error"
                    }`,
                    "error"
                );
                return;
            }

            if (response.ok) {
                // Also update local state with proper file copying
                const approvedFiles = document.new_files
                    ? Array.isArray(document.new_files)
                        ? [...document.new_files]
                        : JSON.parse(JSON.stringify(document.new_files))
                    : null;

                console.log(
                    "🔍 Approved files after processing:",
                    approvedFiles
                );

                const approvedDocument = {
                    ...document,
                    approved: true,
                    files: approvedFiles,
                    new_files: null,
                    status: "approved",
                };

                console.log("🔍 Created approvedDocument:", {
                    id: approvedDocument.id,
                    approved: approvedDocument.approved,
                    status: approvedDocument.status,
                    files: approvedDocument.files,
                    new_files: approvedDocument.new_files,
                    files_type: typeof approvedDocument.files,
                    new_files_type: typeof approvedDocument.new_files,
                    files_length: approvedDocument.files
                        ? Array.isArray(approvedDocument.files)
                            ? approvedDocument.files.length
                            : Object.keys(approvedDocument.files).length
                        : 0,
                });

                console.log(
                    "🔍 Full approvedDocument structure:",
                    JSON.stringify(approvedDocument, null, 2)
                );

                dispatch(
                    updateDocumentUsers(
                        approvedDocument,
                        () => {
                            console.log(
                                "✅ Document approved successfully, refreshing data..."
                            );
                            showAlert(
                                t("success") || "Success",
                                t("document_approved") || "Document approved successfully",
                                "success"
                            );
                            // Local state is now updated immediately by the reducer
                            // No need for delayed refresh as Redux state is updated instantly
                            console.log(
                                "✅ Document approval completed and local state updated"
                            );
                        },
                        () => {
                            showAlert(
                                t("error") || "Error",
                                t("update_failed") || "Failed to update document",
                                "error"
                            );
                        }
                    )
                );
            } else {
                console.error(
                    "❌ updateDocumentStatus failed with status:",
                    response.status
                );
                showAlert(
                    t("error"),
                    responseData?.error || t("update_failed"),
                    "error"
                );
            }
        } catch (error) {
            console.error("🔍 Error approving document:", error);
            showAlert(
                t("error") || "Error",
                t("update_failed") || "Failed to approve document",
                "error"
            );
        }
    };

    const handleReject = async (document, isRemove) => {
        console.log(
            "🔍 Opening reject dialog for document:",
            document.id,
            "isRemove:",
            isRemove
        );
        // If isRemove is true, we need to show dialog first
        // Otherwise, we can proceed directly
        if (!isRemove) {
            setRejectDialog({
                open: true,
                document: document,
                reason: "",
                isRemove: isRemove,
            });
        } else {
            // For remove case, we still need reason, so show dialog
            setRejectDialog({
                open: true,
                document: document,
                reason: "",
                isRemove: isRemove,
            });
        }
    };

    const handleRejectConfirm = async () => {
        const { document, reason, isRemove } = rejectDialog;

        if (!reason.trim()) {
            showAlert(
                t("error") || "Error",
                t("rejection_reason_required") ||
                    "Rejection reason is required",
                "error"
            );
            return;
        }

        console.log(
            "🔍 Rejecting document:",
            document.id,
            "with reason:",
            reason
        );

        try {
            // Close dialog first
            setRejectDialog({
                open: false,
                document: null,
                reason: "",
                isRemove: false,
            });

            // ALWAYS send notification first, then handle local deletion if needed
            console.log(
                "🔍 Calling updateDocumentStatus for rejection:",
                document.id
            );
            const headers = await getAuthHeaders();
            const response = await fetch(
                `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net/updateDocumentStatus`,
                {
                    method: "POST",
                    headers,
                    body: JSON.stringify({
                        documentId: document.id,
                        status: "rejected",
                        reason: reason.trim(),
                    }),
                }
            );

            console.log(
                "🔍 updateDocumentStatus response status:",
                response.status
            );
            const responseData = await response.json();
            console.log("🔍 updateDocumentStatus response data:", responseData);

            // Then handle local state update
            if (isRemove) {
                dispatch(
                    deleteDocumentUser(
                        document.id,
                        () => {
                            showAlert(
                                t("success") || "Success",
                                t("document_deleted") || "Document deleted successfully",
                                "success"
                            );
                        },
                        () => {
                            showAlert(
                                t("error") || "Error",
                                t("delete_failed") || "Failed to delete document",
                                "error"
                            );
                        }
                    )
                );
            } else {
                // Also update local state
                const rejectedDocument = {
                    ...document,
                    approved: false,
                    files: null,
                    new_files: null,
                    status: "rejected",
                    reason: reason.trim(),
                };

                if (response.ok) {
                    dispatch(
                        updateDocumentUsers(
                            rejectedDocument,
                            () => {
                                showAlert(
                                    t("success") || "Success",
                                    t("document_rejected") || "Document rejected successfully",
                                    "success"
                                );
                            },
                            () => {
                                showAlert(
                                    t("error") || "Error",
                                    t("update_failed") || "Failed to update document",
                                    "error"
                                );
                            }
                        )
                    );
                } else {
                    console.error(
                        "❌ updateDocumentStatus failed with status:",
                        response.status
                    );
                    showAlert(
                        t("error") || "Error",
                        responseData?.error || t("update_failed") || "Failed to update document",
                        "error"
                    );
                }
            }
        } catch (error) {
            console.error("🔍 Error rejecting document:", error);
            showAlert(
                t("error") || "Error",
                t("update_failed") || "Failed to reject document",
                "error"
            );
        }
    };

    const handleRejectCancel = () => {
        setRejectDialog({
            open: false,
            document: null,
            reason: "",
            isRemove: false,
        });
    };

    console.log("🔍 DocumentManagementMenu rendering with:", {
        documents,
        documentUsersList,
        open,
    });

    // Simple test to show if component is loading
    if (!documentFields || !documentUsers) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" component="h1">
                    {t("document_management")}
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Loading document management data...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* New Document Notification */}
            {newDocumentCount > 0 && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    onClose={() => setNewDocumentCount(0)}
                >
                    {t("new_documents_uploaded", { count: newDocumentCount })}
                </Alert>
            )}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Typography variant="h5" component="h1">
                    {t("document_management")}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    sx={{ borderRadius: 2 }}
                    onClick={handleOpen}
                >
                    {t("new_document")}
                </Button>
            </Box>

            <CreateDocumentDialog
                open={open}
                onClose={handleClose}
                onSubmit={handleCreateDocument}
                editDocument={editDocument}
            />

            <DocumentFieldTable
                documents={documents}
                onEditDocument={(document) => handleEditDocument(document)}
                onToggleState={handleToggleState}
                onToggleRequired={handleToggleRequired}
            />

            <DocumentUserTable
                documentUsers={documentUsersList}
                onApprove={handleApprove}
                onReject={handleReject}
            />

            <AlertDialog
                open={alertDialog.open}
                onClose={closeAlert}
                title={alertDialog.title}
                message={alertDialog.message}
                type={alertDialog.type}
            />

            {/* Rejection Reason Dialog */}
            <Dialog
                open={rejectDialog.open}
                onClose={handleRejectCancel}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t("reject_document") || "Reject Document"}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {t("document_name") || "Document"}:{" "}
                            <strong>
                                {rejectDialog.document?.field?.name}
                            </strong>
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ mb: 2, color: "text.secondary" }}
                        >
                            {t("user") || "User"}:{" "}
                            {rejectDialog.document?.user_name} (
                            {rejectDialog.document?.user_email})
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label={t("rejection_reason") || "Rejection Reason"}
                            value={rejectDialog.reason}
                            onChange={(e) =>
                                setRejectDialog((prev) => ({
                                    ...prev,
                                    reason: e.target.value,
                                }))
                            }
                            placeholder={
                                t("rejection_reason_placeholder") ||
                                "Please provide a clear reason for rejecting this document..."
                            }
                            required
                            sx={{ mb: 2 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {t("rejection_reason_help") ||
                                "This reason will be sent to the user in the notification."}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRejectCancel} color="inherit">
                        {t("cancel") || "Cancel"}
                    </Button>
                    <Button
                        onClick={handleRejectConfirm}
                        color="error"
                        variant="contained"
                        disabled={!rejectDialog.reason.trim()}
                    >
                        {t("reject") || "Reject"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DocumentManagementMenu;
